import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Calendar, Users, MapPin, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getUserLanguage = () => {
  const override = localStorage.getItem('arctic_riders_lang');
  if (override === 'sv' || override === 'en') return override;
  return 'sv';
};

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(getUserLanguage());

  useEffect(() => {
    const onLangChange = () => setLang(getUserLanguage());
    window.addEventListener('langchange', onLangChange);
    window.addEventListener('storage', onLangChange);
    return () => {
      window.removeEventListener('langchange', onLangChange);
      window.removeEventListener('storage', onLangChange);
    };
  }, []);

  const t = (sv, en) => lang === 'sv' ? sv : en;
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/booking/details/${sessionId}`);
        if (response.ok) {
          const data = await response.json();
          setBooking(data);
        }
      } catch (error) {
        console.error('Error fetching booking:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [sessionId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatPrice = (price) => `${Number(price || 0).toLocaleString('sv-SE')} kr`;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h1 className="font-anton text-4xl md:text-5xl text-white mb-4" data-testid="thankyou-title">
            {t('BOKNING BEKRÄFTAD!', 'BOOKING CONFIRMED!')}
          </h1>
          <p className="text-white/70 text-lg">
            {t('Tack för din bokning hos Arctic Riders. Ditt äventyr väntar!', 'Thank you for booking with Arctic Riders. Your adventure awaits!')}
          </p>
        </div>

        {loading ? (
          <div className="bg-[#171717] border border-white/10 p-8 text-center">
            <div className="animate-pulse text-white/50">{t('Laddar bokningsdetaljer...', 'Loading booking details...')}</div>
          </div>
        ) : booking ? (
          <div className="bg-[#171717] border border-white/10 p-8 mb-8">
            <h2 className="font-anton text-2xl text-[#EA580C] mb-6">{t('DIN BOKNING', 'YOUR BOOKING')}</h2>

            <div className="space-y-4">
              <div className="flex items-start gap-4 pb-4 border-b border-white/10">
                <MapPin className="w-5 h-5 text-[#EA580C] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white/50 text-sm">{t('Paket', 'Package')}</p>
                  <p className="text-white text-lg font-semibold">{booking.package_name}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 pb-4 border-b border-white/10">
                <Calendar className="w-5 h-5 text-[#EA580C] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white/50 text-sm">{t('Datum', 'Date')}</p>
                  <p className="text-white text-lg">
                    {formatDate(booking.date)}
                    {booking.days > 1 && booking.end_date && (
                      <span className="text-white/70"> — {formatDate(booking.end_date)}</span>
                    )}
                  </p>
                  {booking.days > 1 && (
                    <p className="text-white/50 text-sm">{booking.days} {t('dagar', 'days')}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-4 pb-4 border-b border-white/10">
                <Users className="w-5 h-5 text-[#EA580C] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white/50 text-sm">{t('Förare', 'Riders')}</p>
                  <p className="text-white text-lg">{booking.riders} {t(booking.riders === 1 ? 'förare' : 'förare', booking.riders === 1 ? 'rider' : 'riders')}</p>
                </div>
              </div>

              <div className="pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/70">{t('Totalt betalat', 'Total Paid')}</span>
                  <span className="font-anton text-3xl text-[#EA580C]">
                    {formatPrice(booking.total_price)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-[#171717] border border-white/10 p-8 mb-8 text-center">
            <p className="text-white/70">{t('Din bokning är bekräftad. Kolla din e-post för detaljer.', 'Your booking has been confirmed. Check your email for details.')}</p>
          </div>
        )}

        <div className="bg-[#171717] border border-white/10 p-8 mb-8">
          <h2 className="font-anton text-2xl text-white mb-4">{t('NÄSTA STEG?', "WHAT'S NEXT?")}</h2>
          <ul className="space-y-3 text-white/70">
            <li className="flex items-start gap-3">
              <span className="text-[#EA580C] font-bold">1.</span>
              <span>{t('Kolla din e-post för bekräftelsen med alla detaljer', 'Check your email for the booking confirmation with all details')}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#EA580C] font-bold">2.</span>
              <span>{t('Kom 20 minuter innan utsatt tid', 'Arrive 20 minutes before your scheduled time')}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#EA580C] font-bold">3.</span>
              <span>{t('Ta med pass och giltigt motorcykelkörkort', 'Bring your passport and valid motorcycle license')}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#EA580C] font-bold">4.</span>
              <span>{t('Klä dig efter Stockholms växlande väder', 'Wear appropriate clothing for changing Stockholm weather')}</span>
            </li>
          </ul>
        </div>

        <div className="text-center text-white/50 mb-8">
          <p>{t('Frågor? Kontakta oss på', 'Questions? Contact us at')} <a href="mailto:info@arcticriders.com" className="text-[#EA580C] hover:underline">info@arcticriders.com</a></p>
          <p>{t('eller ring', 'or call')} <a href="tel:+46707445405" className="text-[#EA580C] hover:underline">+46 70 744 5405</a></p>
        </div>

        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#EA580C] text-white px-8 py-4 font-anton text-lg uppercase tracking-wider hover:bg-[#EA580C]/90 transition-colors"
            data-testid="back-to-home-btn"
          >
            {t('Tillbaka till startsidan', 'Back to Home')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
